import { useEffect, useState, useRef } from "react";
import { Loader2, Upload as UploadIcon, RotateCcw, Save, ShieldAlert, Lock, LogOut, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useTitle } from "@/components/PageHeader";
import { api, adminApi, asset } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfig";
import { useAdminAuth } from "@/context/AdminAuth";
import { IMAGE_SECTIONS, SECTION_DEFAULTS, LOGO } from "@/data/content";
import { StaffEditor } from "@/components/admin/StaffEditor";
import { RulesEditor } from "@/components/admin/RulesEditor";
import { StreamersEditor } from "@/components/admin/StreamersEditor";
import { JobsEditor, PenalEditor } from "@/components/admin/JobsPenalEditor";
import { BusinessesEditor } from "@/components/admin/BusinessesEditor";
import { GalleryEditor } from "@/components/admin/GalleryEditor";
import { RanchesEditor } from "@/components/admin/RanchesEditor";
import { HousesEditor } from "@/components/admin/HousesEditor";
import { GuideEditor } from "@/components/admin/GuideEditor";
import { LoreEditor } from "@/components/admin/LoreEditor";
import { BizRulesEditor, BizAlertsEditor } from "@/components/admin/BizContentEditor";
import { BizApplyEditor } from "@/components/admin/BizApplyEditor";
import { GroupEditor } from "@/components/admin/GroupEditor";
import { ImageField } from "@/components/admin/ImageField";
import { PenalTermsEditor, AggravatingEditor, ControlledEditor, IllegalEditor, ProvisionsEditor, PenaltiesEditor, WarningsEditor, PenalTextEditor, BizPolicyEditor, BizSalesEditor, HomeEditor } from "@/components/admin/PenalLegalEditor";

const Divider = () => <div className="my-10 border-t border-[#2A201A]" />;

/* ---------- Login gate ---------- */
const LoginGate = () => {
  const { login } = useAdminAuth();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(pw); toast.success("Welcome back."); }
    catch { toast.error("Incorrect password."); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-[#2A201A] bg-surface/40 p-8" data-testid="admin-login-form">
        <img src={LOGO} alt="" className="h-16 w-16 rounded-full ring-1 ring-gold/40 mx-auto" />
        <h1 className="mt-6 text-center font-display text-3xl font-light">Control Room</h1>
        <p className="mt-2 text-center text-sm text-[#8F877C]">Enter the admin password to continue.</p>
        <div className="mt-8 relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F877C]" />
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-password-input"
            placeholder="Password" autoFocus
            className="w-full bg-obsidian border border-[#2A201A] pl-11 pr-4 py-3 text-sm text-bone focus:outline-none focus:border-teal" />
        </div>
        <button type="submit" disabled={busy} data-testid="admin-login-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-6 py-3.5 hover:bg-bone transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter"}
        </button>
      </form>
    </div>
  );
};

/* ---------- Appearance tab ---------- */
const SectionRow = ({ section, current, onSet, onReset }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Choose an image file."); return; }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await adminApi.post("/upload", form, { params: { scope: "site" }, headers: { "Content-Type": "multipart/form-data" } });
      onSet(section.key, data.url);
      toast.success(`${section.label} updated — remember to Save.`);
    } catch { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };
  const preview = current || SECTION_DEFAULTS[section.key];
  const overridden = !!current;
  return (
    <div className="flex items-center gap-5 border border-[#2A201A] bg-surface/40 p-4" data-testid={`admin-section-${section.key}`}>
      <div className="relative h-20 w-32 shrink-0 overflow-hidden border border-[#2A201A]">
        <img src={asset(preview)} alt={section.label} className="h-full w-full object-cover" />
        {overridden && <span className="absolute top-1 left-1 font-mono text-[9px] uppercase tracking-wider bg-teal text-obsidian px-1.5">custom</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-feature text-sm text-bone">{section.label}</p>
        <p className="font-mono text-[10px] text-[#8F877C] truncate">{overridden ? "Custom image" : "Default image"}</p>
      </div>
      <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`admin-upload-${section.key}`}
        className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-4 py-2.5 hover:border-teal hover:text-teal transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadIcon className="h-4 w-4" />} Replace
      </button>
      {overridden && (
        <button onClick={() => onReset(section.key)} className="p-2 text-[#8F877C] hover:text-destructive transition-colors" title="Reset to default">
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

const Appearance = () => {
  const { refresh } = useSiteConfig();
  const [colors, setColors] = useState({ primary: "#3FC7AD", accent: "#C79F5B" });
  const [images, setImages] = useState({});
  const [docs, setDocs] = useState({ rules: "", penal: "" });
  const [links, setLinks] = useState({ connect: "", discord: "", joinCode: "", countyApp: "", whitelistApp: "", ticket: "" });
  const [logo, setLogo] = useState("");
  const [footerTagline, setFooterTagline] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/site-config");
      setColors({ primary: data.colors?.primary || "#3FC7AD", accent: data.colors?.accent || "#C79F5B" });
      setImages(data.images || {});
      setDocs({ rules: data.docs?.rules || "", penal: data.docs?.penal || "" });
      setLinks({ connect: data.links?.connect || "", discord: data.links?.discord || "", joinCode: data.links?.joinCode || "", countyApp: data.links?.countyApp || "", whitelistApp: data.links?.whitelistApp || "", ticket: data.links?.ticket || "" });
      setLogo(data.logo || "");
      setFooterTagline(data.footer_tagline || "");
    })();
  }, []);
  const setImg = (key, url) => setImages((p) => ({ ...p, [key]: url }));
  const resetImg = (key) => setImages((p) => { const n = { ...p }; delete n[key]; return n; });
  const save = async () => {
    setSaving(true);
    try { await adminApi.put("/site-config", { colors, images, docs, links, logo, footer_tagline: footerTagline }); await refresh(); toast.success("Saved! Live across the site."); }
    catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-12">
      <div>
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-6">Server Links &amp; Branding</p>
        <p className="text-sm text-[#8F877C] mb-4">These drive the Connect / Join Discord buttons across the site, the footer, and the logo. Update them here whenever your cfx code, Discord invite, or branding changes.</p>
        <div className="mb-5 max-w-md">
          <ImageField value={logo} onChange={setLogo} label="Logo (upload or paste URL)" scope="site" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Direct Connect URL</p>
            <input value={links.connect} onChange={(e) => setLinks((p) => ({ ...p, connect: e.target.value }))} data-testid="admin-link-connect"
              placeholder="https://cfx.re/join/xxxxxx"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Discord Invite URL</p>
            <input value={links.discord} onChange={(e) => setLinks((p) => ({ ...p, discord: e.target.value }))} data-testid="admin-link-discord"
              placeholder="https://discord.gg/xxxxxx"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">cfx Join Code (shown as cfx.re/join/…)</p>
            <input value={links.joinCode} onChange={(e) => setLinks((p) => ({ ...p, joinCode: e.target.value }))} data-testid="admin-link-joincode"
              placeholder="4dxkmv"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Footer tagline</p>
            <input value={footerTagline} onChange={(e) => setFooterTagline(e.target.value)} data-testid="admin-footer-tagline"
              placeholder="A whitelisted RedM roleplay frontier…"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">County Applications URL (Discord)</p>
            <input value={links.countyApp} onChange={(e) => setLinks((p) => ({ ...p, countyApp: e.target.value }))} data-testid="admin-link-countyapp"
              placeholder="https://discord.com/channels/…"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Whitelist Application URL (Discord)</p>
            <input value={links.whitelistApp} onChange={(e) => setLinks((p) => ({ ...p, whitelistApp: e.target.value }))} data-testid="admin-link-whitelistapp"
              placeholder="https://discord.com/channels/…"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Open Ticket URL (Discord)</p>
            <input value={links.ticket} onChange={(e) => setLinks((p) => ({ ...p, ticket: e.target.value }))} data-testid="admin-link-ticket"
              placeholder="https://discord.com/channels/…"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
        </div>
      </div>
      <div>
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-6">Documents (Google Docs)</p>
        <p className="text-sm text-[#8F877C] mb-4">Paste a Google Doc link (set to "Anyone with the link → Viewer"). The Rules / Penal Code pages embed it and stay in sync as you edit the doc. Leave blank to use the built-in editable tables instead.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Rules doc URL</p>
            <input value={docs.rules} onChange={(e) => setDocs((p) => ({ ...p, rules: e.target.value }))} data-testid="admin-doc-rules"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Penal Code doc URL</p>
            <input value={docs.penal} onChange={(e) => setDocs((p) => ({ ...p, penal: e.target.value }))} data-testid="admin-doc-penal"
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
          </div>
        </div>
      </div>
      <div>
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-6">Brand Colors</p>
        <div className="grid gap-6 sm:grid-cols-2">
          {["primary", "accent"].map((k) => (
            <div key={k} className="flex items-center gap-4 border border-[#2A201A] bg-surface/40 p-5">
              <input type="color" value={colors[k]} data-testid={`admin-color-${k}`}
                onChange={(e) => setColors((p) => ({ ...p, [k]: e.target.value }))}
                className="h-14 w-14 cursor-pointer bg-transparent border border-[#2A201A]" />
              <div className="flex-1">
                <p className="font-feature text-sm capitalize">{k} color</p>
                <input value={colors[k]} onChange={(e) => setColors((p) => ({ ...p, [k]: e.target.value }))}
                  className="mt-1 w-full bg-obsidian border border-[#2A201A] px-3 py-2 font-mono text-xs text-bone focus:outline-none focus:border-teal" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-6">Section Images</p>
        <div className="space-y-3">
          {IMAGE_SECTIONS.map((s) => (
            <SectionRow key={s.key} section={s} current={images[s.key]} onSet={setImg} onReset={resetImg} />
          ))}
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-save-btn"
        className="inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-8 py-4 hover:bg-bone transition-colors disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Appearance
      </button>
    </div>
  );
};

/* ---------- Security tab ---------- */
const Security = () => {
  const { logout } = useAdminAuth();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const change = async () => {
    if (pw.length < 4) { toast.error("At least 4 characters."); return; }
    setBusy(true);
    try { await adminApi.put("/admin/password", { new_password: pw }); toast.success("Password updated."); setPw(""); }
    catch { toast.error("Failed to update."); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md space-y-8">
      <div>
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-4">Change Admin Password</p>
        <div className="relative">
          <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F877C]" />
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-newpw-input"
            placeholder="New password"
            className="w-full bg-obsidian border border-[#2A201A] pl-11 pr-4 py-3 text-sm text-bone focus:outline-none focus:border-teal" />
        </div>
        <button onClick={change} disabled={busy} data-testid="admin-change-pw-btn"
          className="mt-4 inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-6 py-3 hover:bg-bone transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
        </button>
      </div>
      <button onClick={logout} data-testid="admin-logout-btn"
        className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-6 py-3 text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors">
        <LogOut className="h-4 w-4" /> Log out
      </button>
    </div>
  );
};

/* ---------- Panel ---------- */
export default function Admin() {
  useTitle("Admin");
  const { authed, logout } = useAdminAuth();
  const [twitchConfigured, setTwitchConfigured] = useState(false);

  useEffect(() => {
    if (authed) api.get("/streamers-live").then(({ data }) => setTwitchConfigured(data.configured)).catch(() => {});
  }, [authed]);

  if (authed === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;
  if (!authed) return <LoginGate />;

  return (
    <div className="pt-28 pb-24">
      <header className="mx-auto max-w-5xl px-6 sm:px-10">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="" className="h-12 w-12 rounded-full ring-1 ring-gold/40" />
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-teal">Control Room</p>
              <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight">Site Manager</h1>
            </div>
          </div>
          <button onClick={logout} className="hidden sm:inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-4 py-2.5 text-[#8F877C] hover:border-teal hover:text-teal transition-colors"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
        <div className="mt-6 flex items-start gap-3 border border-gold/30 bg-gold/5 p-4 text-sm text-[#c9c0b0]" data-testid="admin-warning">
          <ShieldAlert className="h-5 w-5 text-gold shrink-0" />
          <span>You're logged in. Anyone with the password can edit the site — keep it private and change it under <strong className="text-bone">Security</strong>.</span>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-6 sm:px-10 mt-10">
        <Tabs defaultValue="appearance">
          <TabsList className="bg-surface/60 border border-[#2A201A] flex-wrap h-auto">
            <TabsTrigger value="appearance" data-testid="tab-appearance">Appearance</TabsTrigger>
            <TabsTrigger value="home" data-testid="tab-home">Home</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="penal" data-testid="tab-penal">Penal Code</TabsTrigger>
            <TabsTrigger value="penalties" data-testid="tab-penalties">Penalties</TabsTrigger>
            <TabsTrigger value="bizpolicy" data-testid="tab-bizpolicy">Business Policy</TabsTrigger>
            <TabsTrigger value="businesses" data-testid="tab-businesses">Businesses</TabsTrigger>
            <TabsTrigger value="biz-rules" data-testid="tab-biz-rules">Biz Rules</TabsTrigger>
            <TabsTrigger value="biz-alerts" data-testid="tab-biz-alerts">Biz Alerts</TabsTrigger>
            <TabsTrigger value="biz-apply" data-testid="tab-biz-apply">How to Apply</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
            <TabsTrigger value="guide" data-testid="tab-guide">Guide</TabsTrigger>
            <TabsTrigger value="lore" data-testid="tab-lore">Lore</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="ranches" data-testid="tab-ranches">Ranches</TabsTrigger>
            <TabsTrigger value="houses" data-testid="tab-houses">Houses</TabsTrigger>
            <TabsTrigger value="staff" data-testid="tab-staff">Staff</TabsTrigger>
            <TabsTrigger value="tribes" data-testid="tab-tribes">Tribes</TabsTrigger>
            <TabsTrigger value="gangs" data-testid="tab-gangs">Gangs</TabsTrigger>
            <TabsTrigger value="streamers" data-testid="tab-streamers">Streamers</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
          </TabsList>
          <TabsContent value="appearance" className="mt-8"><Appearance /></TabsContent>
          <TabsContent value="home" className="mt-8"><HomeEditor /></TabsContent>
          <TabsContent value="jobs" className="mt-8"><JobsEditor /></TabsContent>
          <TabsContent value="penal" className="mt-8">
            <PenalEditor />
            <Divider /><PenalTermsEditor />
            <Divider /><ProvisionsEditor />
            <Divider /><AggravatingEditor />
            <Divider /><ControlledEditor />
            <Divider /><IllegalEditor />
            <Divider /><PenalTextEditor />
          </TabsContent>
          <TabsContent value="penalties" className="mt-8"><PenaltiesEditor /></TabsContent>
          <TabsContent value="bizpolicy" className="mt-8">
            <BizPolicyEditor />
            <Divider /><BizSalesEditor />
          </TabsContent>
          <TabsContent value="businesses" className="mt-8"><BusinessesEditor /></TabsContent>
          <TabsContent value="biz-rules" className="mt-8"><BizRulesEditor /></TabsContent>
          <TabsContent value="biz-alerts" className="mt-8"><BizAlertsEditor /></TabsContent>
          <TabsContent value="biz-apply" className="mt-8"><BizApplyEditor /></TabsContent>
          <TabsContent value="rules" className="mt-8">
            <RulesEditor />
            <Divider /><WarningsEditor />
          </TabsContent>
          <TabsContent value="guide" className="mt-8"><GuideEditor /></TabsContent>
          <TabsContent value="lore" className="mt-8"><LoreEditor /></TabsContent>
          <TabsContent value="gallery" className="mt-8"><GalleryEditor /></TabsContent>
          <TabsContent value="ranches" className="mt-8"><RanchesEditor /></TabsContent>
          <TabsContent value="houses" className="mt-8"><HousesEditor /></TabsContent>
          <TabsContent value="staff" className="mt-8"><StaffEditor /></TabsContent>
          <TabsContent value="tribes" className="mt-8"><GroupEditor path="tribes" blankName="New Tribe" tagLabel="Tag" /></TabsContent>
          <TabsContent value="gangs" className="mt-8"><GroupEditor path="gangs" blankName="New Gang" tagLabel="Type" /></TabsContent>
          <TabsContent value="streamers" className="mt-8"><StreamersEditor twitchConfigured={twitchConfigured} /></TabsContent>
          <TabsContent value="security" className="mt-8"><Security /></TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
